import { debounce } from '../helpers/debounce';

/**
 * ArchedTitle — Custom element that renders one or more lines of text along a circular arc.
 *
 * Relies on window.CircleType (loaded via src/main.js) to distribute characters along the curve.
 *
 * --- Data attributes (set by arched-title.liquid) ---
 * data-title        Pipe-separated lines of text, e.g. "FEEL GOOD|ABOUT CANDY".
 *                   The liquid block converts </p> tags to | before stripping HTML,
 *                   so a two-paragraph richtext field becomes two arched lines.
 * data-font-color   CSS color string for the main text fill.
 * data-shadow-color CSS color string for the shadow/stroke layer.
 * data-curve-depth  Integer 1–10. 10 = tightest arch, 1 = nearly flat.
 *                   Mapped to a CircleType radius multiplier: 10 → 0.8×, 1 → 8×.
 *
 * --- Layering approach ---
 * Each line is rendered as two overlapping divs inside a CSS grid cell:
 *   1. shadowDiv  — shadow_color fill + stroke + CSS filter drop-shadows going
 *                   diagonally bottom-right. filter: drop-shadow() is used (not
 *                   text-shadow) because drop-shadow composites against the full
 *                   rendered element, including the stroke, giving a gapless shadow.
 *   2. textDiv    — font_color fill, no stroke. Sits on top, hiding the pink body
 *                   of shadowDiv; only the shadow bleeds out from behind.
 *
 * --- Font sizing ---
 * Rather than a blind grow-loop, we:
 *   1. Measure raw scrollWidth at a known base size (before CircleType wraps chars).
 *   2. Calculate the target arc length from the chord/radius geometry.
 *   3. Scale font size proportionally (one shot, close to correct).
 *   4. Refine with up to 20 rAF iterations of +0.5px nudges in case of
 *      sub-pixel font rendering differences. All lines share the same font
 *      size, driven by the widest line.
 *
 * --- Resize handling ---
 * A ResizeObserver watches the *parent element* (not this element) to avoid a
 * feedback loop where CircleType's own DOM mutations would re-trigger the observer.
 * A width-change guard (`_lastWidth`) provides a second layer of protection:
 * the refitting only runs when the container width has genuinely changed.
 * Responses are debounced at 200 ms to avoid thrashing during drag-resize.
 */
export default class ArchedTitle extends HTMLElement {

  connectedCallback() {
    this.fontColor = this.dataset.fontColor;
    this.shadowColor = this.dataset.shadowColor;

    // Shopify range slider: 1 (flattest) → 10 (most curved).
    // Map linearly to a CircleType radius multiplier: depth 10 → 0.8×, depth 1 → 8×.
    const depth = parseInt(this.dataset.curveDepth ?? 8, 10);
    this._radiusMultiplier = 0.8 + ((10 - depth) / 9) * (8 - 0.8);

    // The liquid block converts </p> to | so multiple richtext paragraphs
    // become multiple arched lines.
    this.titleLines = this.dataset.title.split('|').map(l => l.trim()).filter(Boolean);

    this.style.display = 'flex';
    this.style.flexDirection = 'column';
    this.style.alignItems = 'center';
    this.style.overflow = 'visible';
    this.style.width = '100%';

    // filter: drop-shadow() includes the stroke in the shadow shape, unlike
    // text-shadow which only shadows the fill. Safari composites drop-shadow
    // differently to Chrome, requiring more steps to fill gaps.
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    const shadowCount = isSafari ? 12 : 6;
    const shadowSteps = Array.from({ length: shadowCount }, (_, i) => {
      const o = ((i + 1) * 0.01).toFixed(2);
      return `drop-shadow(${o}em ${o}em 0 ${this.shadowColor})`;
    }).join(' ');

    const sharedStyles = {
      whiteSpace: 'nowrap',
      paintOrder: 'stroke fill',
      lineHeight: '0.8'
    };

    // Build a wrapper + div pair for each line and insert them before the sr-only element.
    this.lineEls = this.titleLines.map(line => {
      const wrapper = document.createElement('div');
      wrapper.style.display = 'grid';
      wrapper.style.flexShrink = '0';
      wrapper.style.minWidth = '100%';

      // Layer 1 — shadow (behind)
      const shadowDiv = document.createElement('div');
      shadowDiv.className = 'arched-text-shadow font-display-secondary';
      Object.assign(shadowDiv.style, {
        ...sharedStyles,
        gridArea: '1/1',
        color: this.shadowColor,
        WebkitTextStroke: `5px ${this.shadowColor}`,
        filter: shadowSteps,
      });
      shadowDiv.textContent = line;

      // Layer 2 — main text (front)
      const textDiv = document.createElement('div');
      textDiv.className = 'arched-text font-display-secondary';
      Object.assign(textDiv.style, {
        ...sharedStyles,
        gridArea: '1/1',
        color: this.fontColor,
      });
      textDiv.textContent = line;

      wrapper.append(shadowDiv, textDiv);
      this.querySelector('.sr-only').before(wrapper);

      return { wrapper, shadowDiv, textDiv, line };
    });

    this._lastWidth = 0;
    this.fit();

    // Observe this element directly. CircleType only mutates height, not width,
    // so the _lastWidth guard below prevents the feedback loop without needing
    // to watch the parent (which can be unreliable inside container-query contexts).
    this._onResize = debounce(() => {
      const width = this.offsetWidth;
      if (width === this._lastWidth) return; // guard: height changes don't trigger refit

      this.lineEls.forEach(el => {
        el.ct?.destroy();       // restores original text node, removing CircleType's spans
        el.ctShadow?.destroy();
        el.textDiv.textContent = el.line;
        el.shadowDiv.textContent = el.line;
      });
      this.fit();
    }, 200);

    this.resizeObserver = new ResizeObserver(this._onResize);
    this.resizeObserver.observe(this);
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
  }

  fit() {
    // width:100% is set in connectedCallback so this.offsetWidth reliably
    // reflects the parent container's width, not the shrink-wrapped content.
    const containerWidth = this.offsetWidth;

    // Guard: if the parent hasn't been laid out yet, defer until next frame.
    if (!containerWidth) {
      requestAnimationFrame(() => this.fit());
      return;
    }

    // A larger radius multiplier = flatter arch. Derived from data-curve-depth.
    const radius = containerWidth * this._radiusMultiplier;

    // Target chord: text spans 90% of container width.
    // Arc angle is derived from the chord/radius relationship: θ = 2·arcsin(chord / 2r)
    const chordWidth = containerWidth * 0.9;
    const targetAngleRad = 2 * Math.asin(chordWidth / (2 * radius));
    const targetArcLength = targetAngleRad * radius;

    // Measures actual text content width using a Range, which returns the tight
    // bounding box of the text nodes regardless of the element's layout box size.
    // scrollWidth is unreliable here because the grid wrapper (minWidth: 100%)
    // forces the textDiv to stretch to container width, making scrollWidth always
    // return the container width instead of the text content width.
    const getTextWidth = (el) => {
      const range = document.createRange();
      range.selectNodeContents(el);
      return range.getBoundingClientRect().width;
    };

    // Step 1 — measure raw text width at a known base size, before CircleType
    // wraps characters in spans.
    const fontSizeBase = 30;
    this.lineEls.forEach(({ textDiv, shadowDiv }) => {
      textDiv.style.fontSize = `${fontSizeBase}px`;
      shadowDiv.style.fontSize = `${fontSizeBase}px`;
    });

    // Step 2 — scale proportionally using the widest line as the reference.
    const maxBaseWidth = Math.max(...this.lineEls.map(({ textDiv }) => getTextWidth(textDiv)));
    let size = fontSizeBase * targetArcLength / maxBaseWidth;

    this.lineEls.forEach(({ textDiv, shadowDiv }) => {
      textDiv.style.fontSize = `${size}px`;
      shadowDiv.style.fontSize = `${size}px`;
    });

    // Step 3 — refine with small nudges before handing off to CircleType.
    // Capped at 100 frames to prevent runaway loops if fonts are slow to load.
    const widestTextDiv = this.lineEls.reduce(
      (max, el) => getTextWidth(el.textDiv) > getTextWidth(max) ? el.textDiv : max,
      this.lineEls[0].textDiv
    );

    let attempts = 0;
    const refine = () => {
      if (attempts++ > 100 || getTextWidth(widestTextDiv) >= targetArcLength - 0.5) {
        // Step 4 — commit: record width so the resize guard ignores the
        // reflows CircleType is about to cause.
        this._lastWidth = containerWidth;
        this.lineEls.forEach(el => {
          el.ctShadow = new window.CircleType(el.shadowDiv).radius(radius).dir(1);
          el.ct = new window.CircleType(el.textDiv).radius(radius).dir(1);
        });
        // Pull subsequent lines up so text is nearly flush.
        // CircleType sets a tall height (the full arc height) on each wrapper,
        // mostly empty space below the characters. Subtract that empty space,
        // leaving just enough room for the text itself.
        if (this.lineEls.length > 1) {
          this.lineEls.slice(1).forEach(el => {
            // Arc depth = the vertical space the curve occupies below the text.
            // Subtracting it pulls subsequent lines up to be nearly flush.
            const arcDepth = radius * (1 - Math.cos(targetAngleRad / 2));
            el.wrapper.style.marginTop = `-${arcDepth}px`;
          });
        }
        return;
      }
      size += 0.5;
      this.lineEls.forEach(({ textDiv, shadowDiv }) => {
        textDiv.style.fontSize = `${size}px`;
        shadowDiv.style.fontSize = `${size}px`;
      });
      requestAnimationFrame(refine);
    };
    requestAnimationFrame(refine);
  }
}
